import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { all } from 'rsvp'
import type SessionService from 'teacher-ui-client/services/session'
import type PartnerService from 'teacher-ui-client/services/partner'
import { lastExpiringOrExpiredSchoolSub } from 'teacher-ui-client/school-subscription/utils'
import type Store from '@ember-data/store'
import { assert } from '@ember/debug'
import type SchoolSubscription from 'teacher-ui-client/models/school-subscription'
import type SchoolTrialCount from 'teacher-ui-client/models/school-trial-count'
import type PartnerContactDetail from 'teacher-ui-client/models/partner-contact-detail'

/**
 * This page is for managing the school-subscription.
 * Teacher must be a subco in order to see this page.
 *
 * Note the following edge cases;
 * - a trial teacher can be a subco (so the utilisation record will be linked to a teacher-sub)
 * - a school may not have a school-subscription yet
 * - a school may only have expired school-subscriptions
 * - school-subscriptions can have a limited or unlimited capacity
 */
export default class ManageSubscriptionRoute extends Route {
  @service partner!: PartnerService

  @service session!: SessionService

  @service store!: Store

  async model() {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    const school = this.session.currentSchool

    // We'll use the last school-subscription when displaying subscription details on the page
    const lastSchoolSubscription = lastExpiringOrExpiredSchoolSub(
      this.store.peekAll<SchoolSubscription>('school-subscription'),
      subscriptionType,
    )

    const partner = this.partner.partnerForSubscriptionType(subscriptionType)
    assert('requires partner', partner)

    const [partnerContactDetailsAll, schoolTrialCounts] = await all([
      this.store.findAll<PartnerContactDetail>('partner-contact-detail', {
        adapterOptions: { partnerId: partner?.id },
      }),
      this.store.findRecord<SchoolTrialCount>('school-trial-count', `${school.id}_${subscriptionType}`, {
        adapterOptions: { subscriptionType, schoolId: school.id },
      }),
    ])
    const partnerContactDetails = partnerContactDetailsAll.find(
      (details) => details.subscriptionType === subscriptionType,
    )
    assert('requires partner contact details', partnerContactDetails)

    return {
      partnerEmail: partnerContactDetails.schoolSupportEmail,
      partnerName: partner.name,
      partnerPhone: partnerContactDetails.schoolSupportPhone,
      lastSchoolSubscription,
      subscriptionType,
      trialStudentCount: schoolTrialCounts.studentTrialCount,
      trialTeacherCount: schoolTrialCounts.teacherTrialCount,
    }
  }
}
